import { Link } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import type { Chore, Member } from "@/lib/schemas";
import { Button } from "@/components/ui/Button";
import { Pill } from "@/components/ui/Pill";
import { Coin } from "@/components/ui/Coin";
import { getXP } from "@/lib/utils/xp";
import { sportUnitLabel } from "@/lib/utils/sport";

type Status = "approved" | "pending" | "rejected" | undefined;

type Props = {
  chore: Chore;
  status: Status;
  /** Kto zrealizował to zadanie dziś — pokazujemy avatar przy ✓ (parent view). */
  assignee?: Member | null;
  /** Pokazuj XP zamiast PLN (rodzic-do-siebie, parent dashboard). */
  asXpOnly?: boolean;
  /** Czy bieżący user może zgłosić "zrobione" — pokazuje przycisk Zrób!. */
  canDoIt?: boolean;
  /** Parent-only ikony edytuj/usuń. */
  canEdit?: boolean;
  /**
   * Rekord życiowy dla sport chore — max sportDone z approved completions.
   * Liczone z useSportRecords. Pokazywany dyskretnie jako "🏆 N".
   */
  sportRecord?: number | null;
  onComplete?: () => void;
  onDelete?: () => void;
  /** Link "Edytuj" — gdy pominięty, kafelek nie renderuje pencil ikony. */
  editHref?: string;
};

/**
 * Jednolita karta zadania dla dashboardu (child + parent self-tasks) oraz
 * dla widoku listy /chores. Zawsze pokazuje znacznik kind: 💛 (family) lub
 * 💰 (extra).
 */
export function ChoreTaskCard({
  chore,
  status,
  assignee,
  asXpOnly = false,
  canDoIt = false,
  canEdit = false,
  sportRecord,
  onComplete,
  onDelete,
  editHref,
}: Props) {
  const isDone = status === "approved";
  const isPending = status === "pending";
  const isRejected = status === "rejected";
  const isSport = chore.kind === "sport";
  const isFamily = chore.kind === "family" || (asXpOnly && !isSport);
  const isXpOnly = isFamily || isSport || asXpOnly;
  const kindIcon = isSport ? "💪" : isXpOnly ? "💛" : "💰";
  const kindLabel = isSport
    ? "Wyzwanie sportowe"
    : isXpOnly
      ? "Pomoc rodzinie"
      : "Zadanie dodatkowe";

  return (
    <div
      className={`rounded-[18px] border-[1.5px] border-line shadow-card transition-opacity ${
        isDone
          ? "bg-soft opacity-60"
          : isSport
            ? "bg-emerald-50"
            : isXpOnly
              ? "bg-[#FFF5D6]"
              : "bg-white"
      }`}
    >
      <div className="p-3 flex items-center gap-3">
        <div className="relative shrink-0">
          <div
            className={`w-12 h-12 rounded-[14px] flex items-center justify-center text-[24px] ${
              isDone ? "bg-cloud" : "bg-hay"
            }`}
            style={
              isDone
                ? undefined
                : { boxShadow: "inset 0 0 0 1.5px var(--kd-hay-bd)" }
            }
            aria-hidden
          >
            {chore.icon ?? "📝"}
          </div>
          <span
            className="absolute -top-1.5 -left-1.5 h-[22px] w-[22px] rounded-full bg-white border border-line shadow-bubble flex items-center justify-center text-[13px] leading-none"
            aria-label={kindLabel}
            title={kindLabel}
          >
            {kindIcon}
          </span>
          {chore.offline && (
            <span
              className="absolute -top-1.5 -right-1.5 h-[22px] w-[22px] rounded-full bg-white border border-line shadow-bubble flex items-center justify-center text-[13px] leading-none"
              aria-label="Aktywność poza ekranem"
              title="Aktywność poza ekranem"
            >
              🌳
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`font-display text-[16px] font-bold truncate ${
              isDone ? "text-slate line-through decoration-1" : "text-navy"
            }`}
          >
            {chore.title}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {isXpOnly ? (
              <span
                className={`font-num text-[13px] font-bold ${
                  isDone
                    ? "text-slate"
                    : isSport
                      ? "text-emerald-700"
                      : "text-[#7A5A00]"
                }`}
              >
                ⚡ +{getXP(isSport ? "sport" : "family", chore.size)} XP
              </span>
            ) : (
              <>
                <Coin size={16} />
                <span
                  className={`font-num text-[13px] font-bold ${
                    isDone ? "text-slate" : "text-forest"
                  }`}
                >
                  +{(chore.rewardMinor / 100).toFixed(2).replace(".", ",")} zł
                </span>
              </>
            )}
            {isSport && typeof chore.sportTarget === "number" && (
              <>
                <span className="text-muted" aria-hidden>·</span>
                <span className="text-[11px] font-semibold text-emerald-700">
                  Cel: {chore.sportTarget} {sportUnitLabel(chore.sportUnit)}
                </span>
              </>
            )}
            {isSport &&
              typeof sportRecord === "number" &&
              sportRecord > 0 && (
                <>
                  <span className="text-muted" aria-hidden>·</span>
                  <span
                    className="text-[11px] font-semibold text-amber-700"
                    aria-label={`Rekord życiowy: ${sportRecord}`}
                  >
                    🏆 {sportRecord}
                  </span>
                </>
              )}
            {assignee && (
              <>
                <span className="text-muted" aria-hidden>·</span>
                <span className="text-[11px] text-muted">
                  {assignee.displayName}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isDone && (
            <div
              className="w-[38px] h-[38px] rounded-full bg-green text-white flex items-center justify-center font-extrabold text-[18px] shadow-cta-flat"
              aria-label="Zatwierdzone"
            >
              ✓
            </div>
          )}
          {isPending && (
            <Pill tone="hay" className="h-8 px-3 text-[12px] font-semibold">
              czeka 🕒
            </Pill>
          )}
          {isRejected && !canDoIt && (
            <span className="inline-flex items-center h-8 px-3 rounded-full text-[12px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
              odrzucone
            </span>
          )}
          {canDoIt && onComplete && (
            <Button
              variant="primary"
              size="md"
              onClick={onComplete}
              className="h-[38px] px-4 text-[14px] rounded-[14px] font-extrabold"
            >
              {isRejected ? "Ponownie" : "Zrób!"}
            </Button>
          )}
          {canEdit && (
            <div className="flex items-center">
              {editHref && (
                <Link
                  to={editHref}
                  className="p-1.5 rounded-[8px] text-muted hover:text-ink hover:bg-soft transition-colors"
                  aria-label="Edytuj"
                  title="Edytuj"
                >
                  <Pencil className="h-4 w-4" />
                </Link>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="p-1.5 rounded-[8px] text-muted hover:text-danger hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                  aria-label="Usuń"
                  title="Usuń"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
